import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import swal from "sweetalert";
import api from "../api/api";
import FarmSelect from "../components/FarmSelect";
import DripForm from "../components/FieldSettingForms/dripForm";
import LateralForm from "../components/FieldSettingForms/lateralForm";
import PivotForm from "../components/FieldSettingForms/pivotForm";
import PageTitle from "../components/common/PageTitle";
import "../components/FieldSettingForms/AgriculturalWizardForms.css";

const IRRIGATION_TYPE_MAP = {
  drip: "drip",
  furrow: "furrow",
  "Irrigation à la raie": "furrow",
  "Irrigation goutte à goutte (bande humide)": "Goutte",
  "Irrigation par planche": "furrow",
  "irrigation par planche (submersion)": "furrow",
  lateral: "lateral",
  "Micro-aspersion": "Micro-aspersion",
  pivot: "pivot",
  sdi: "sdi",
  sprinkler: "sprinkler",
  Goutte: "Goutte",
};

export default function AddIrrigation(props) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isExternalUpdate = useRef(false);
  const previousIrrigationData = useRef(null);

  const { isWizardMode, formData, onChange } = props;
  const parentFieldId = props.fieldId;
  const parentFieldName = props.fieldName;
  const parentZoneId = props.zoneId;
  const parentZoneName = props.zoneName;
  const parentCropId = props.cropId;
  const parentCropName = props.cropName;
  const { zoneId, zoneName } = location.state || {};

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [listIrrigations, setListIrrigations] = useState([]);
  const [listCrop, setListCrop] = useState([]);
  const [crops, setCrops] = useState([]);
  const [irrigData, setIrrigData] = useState({
    irrigType: "",
    zone_uid: parentZoneId || zoneId || "",
    crop_uid: "",
    cropName: parentCropName || "",
    flowrate: "",
    irrigated_already: "",
    name: "",
    pivot_shape: "",
    irrigation_syst: "",
    pivot_length: "",
    pivot_coord: "",
    full_runtime: "",
    lateral: "",
    drippers: "",
    effIrrig: "",
    pumpFlow: "",
    pumpType: "",
    linesNumber: "",
    drippersSpacing: "",
  });

  const handleFieldChange = (fieldName, value) => {
    setIrrigData((previous) => ({ ...previous, [fieldName]: value }));
  };

  const getCropTypeName = (cropTypeId) => {
    const cropType = listCrop.find(
      (item) => item.id === Number(cropTypeId)
    );
    return cropType ? cropType.crop : String(cropTypeId || "");
  };

  useEffect(() => {
    if (!isWizardMode || !formData) return;

    isExternalUpdate.current = true;
    setIrrigData((previous) => {
      const next = {
        ...previous,
        ...formData,
        zone_uid: parentZoneId || zoneId || formData.zone_uid || "",
      };
      const savedCropUid = formData.crop_uid;
      next.crop_uid =
        typeof savedCropUid === "string" && savedCropUid.includes("-")
          ? savedCropUid
          : "";
      previousIrrigationData.current = next;
      return next;
    });
  }, [isWizardMode, formData, parentZoneId, zoneId]);

  useEffect(() => {
    if (!parentZoneId || parentZoneId === irrigData.zone_uid) return;
    isExternalUpdate.current = true;
    setIrrigData((previous) => ({
      ...previous,
      zone_uid: parentZoneId,
    }));
  }, [parentZoneId, irrigData.zone_uid]);

  useEffect(() => {
    if (!isWizardMode || !onChange || isExternalUpdate.current) {
      isExternalUpdate.current = false;
      return;
    }

    const previous = JSON.stringify(previousIrrigationData.current);
    const current = JSON.stringify(irrigData);
    if (previous !== current) {
      previousIrrigationData.current = { ...irrigData };
      onChange(irrigData);
    }
  }, [irrigData, isWizardMode, onChange]);

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      setIsOptionsLoading(true);
      setOptionsError("");
      try {
        const [cropResponse, irrigationResponse] = await Promise.all([
          api.get("/croptype/list-crop"),
          api.get("/irrigations/get-irrigations"),
        ]);
        if (!active) return;
        setListCrop(cropResponse.data.Croptype || []);
        setListIrrigations(irrigationResponse.data.Irrigations || []);
      } catch {
        if (active) setOptionsError(t("irrigation_options_load_error"));
      } finally {
        if (active) setIsOptionsLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, [t]);

  useEffect(() => {
    let active = true;

    const loadZoneCrops = async () => {
      if (!irrigData.zone_uid) {
        setCrops([]);
        return;
      }

      try {
        const response = await api.post("/zone", {
          zone_uid: irrigData.zone_uid,
        });
        if (!active) return;

        const availableCrops = response.data?.zone?.field?.crops || [];
        setCrops(availableCrops);

        const hasValidCropUid =
          typeof irrigData.crop_uid === "string" &&
          irrigData.crop_uid.includes("-");
        if (parentCropId && !hasValidCropUid) {
          const matchingCrop = availableCrops.find(
            (crop) => Number(crop.croptype_id) === Number(parentCropId)
          );
          if (matchingCrop) {
            isExternalUpdate.current = true;
            setIrrigData((previous) => ({
              ...previous,
              crop_uid: String(matchingCrop.uid),
              cropName:
                parentCropName || getCropTypeName(matchingCrop.croptype_id),
            }));
          }
        }
      } catch {
        if (active) setCrops([]);
      }
    };

    loadZoneCrops();
    return () => {
      active = false;
    };
  }, [
    irrigData.zone_uid,
    irrigData.crop_uid,
    parentCropId,
    parentCropName,
    listCrop,
  ]);

  const handleIrrigationType = (event) => {
    const selectedType = event.target.value;
    const irrigation = listIrrigations.find(
      (item) => item.irrigation === selectedType
    );
    setIrrigData((previous) => ({
      ...previous,
      irrigType: selectedType,
      effIrrig: irrigation?.effIrrig ?? previous.effIrrig,
    }));
  };

  const irrigationMethodForm = () => {
    const normalizedType =
      IRRIGATION_TYPE_MAP[irrigData.irrigType] || irrigData.irrigType;

    if (!normalizedType || normalizedType === "none") return null;

    if (normalizedType === "pivot") {
      return (
        <PivotForm
          handleFlowRate={(event) =>
            handleFieldChange("flowrate", event.target.value)
          }
          handleIrrgSyst={(event) =>
            handleFieldChange("irrigation_syst", event.target.value)
          }
          handleRunTime={(event) =>
            handleFieldChange("full_runtime", event.target.value)
          }
          handlePivotCoord={(event) =>
            handleFieldChange("pivot_coord", event.target.value)
          }
          handlePivotLength={(event) =>
            handleFieldChange("pivot_length", event.target.value)
          }
          handlePivotShape={(event) =>
            handleFieldChange("pivot_shape", event.target.value)
          }
          handleName={(event) => handleFieldChange("name", event.target.value)}
          irrigation_syst={irrigData.irrigation_syst}
          pivot_coord={irrigData.pivot_coord}
          pivot_length={irrigData.pivot_length}
          pivot_shape={irrigData.pivot_shape}
          full_runtime={irrigData.full_runtime}
          flowrate={irrigData.flowrate}
          name={irrigData.name}
        />
      );
    }

    if (normalizedType === "lateral") {
      return (
        <LateralForm
          handleLateral={(event) =>
            handleFieldChange("lateral", event.target.value)
          }
          handlePivotLength={(event) =>
            handleFieldChange("pivot_length", event.target.value)
          }
          handleRunTime={(event) =>
            handleFieldChange("full_runtime", event.target.value)
          }
          handleName={(event) => handleFieldChange("name", event.target.value)}
          handleFlowRate={(event) =>
            handleFieldChange("flowrate", event.target.value)
          }
          full_runtime={irrigData.full_runtime}
          flowrate={irrigData.flowrate}
          name={irrigData.name}
          pivot_length={irrigData.pivot_length}
          lateral={irrigData.lateral}
        />
      );
    }

    return (
      <DripForm
        handleDrippers={(event) =>
          handleFieldChange("drippers", event.target.value)
        }
        handleIrrigAlrd={(event) =>
          handleFieldChange("irrigated_already", event.target.value)
        }
        handleFlowRate={(event) =>
          handleFieldChange("flowrate", event.target.value)
        }
        handleDrippersSpacing={(event) =>
          handleFieldChange("drippersSpacing", event.target.value)
        }
        flowrate={irrigData.flowrate}
        drippers={irrigData.drippers}
        irrigated_already={irrigData.irrigated_already}
        drippersSpacing={irrigData.drippersSpacing}
      />
    );
  };

  const addIrrigation = async () => {
    const data = {
      type: irrigData.irrigType,
      zone_uid: irrigData.zone_uid || parentZoneId || zoneId,
      crop_uid: irrigData.crop_uid,
      flowrate: irrigData.flowrate,
      irrigated_already: irrigData.irrigated_already,
      name: irrigData.name,
      pivot_shape: irrigData.pivot_shape,
      irrigation_syst: irrigData.irrigation_syst,
      pivot_length: irrigData.pivot_length,
      pivot_coord: irrigData.pivot_coord,
      full_runtime: irrigData.full_runtime,
      lateral: irrigData.lateral,
      drippers: irrigData.drippers,
      effIrrig: irrigData.effIrrig,
      pumpFlow: irrigData.pumpFlow,
      pumpType: irrigData.pumpType,
      lines_number: irrigData.linesNumber,
      drippers_spacing: irrigData.drippersSpacing,
    };

    const response = await api.post("/irrigation/add-irrigation", data);
    if (response.data.type === "danger") {
      throw new Error(response.data.message || t("Error"));
    }

    if (response.data.type === "success" && !isWizardMode) {
      const message = zoneId
        ? t("Congratulations, you have successfully created a farm!")
        : t("Congratulations, you have successfully created an irrigation!");
      await swal(message, { icon: "success" });
      navigate("/AddField");
    }

    return response.data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    if (isWizardMode) {
      onChange?.(irrigData);
      props.onSubmit?.(event);
      return;
    }

    setIsSubmitting(true);
    try {
      await addIrrigation();
      setValidated(false);
    } catch (error) {
      swal({
        title: t("Error"),
        text: error.response?.data?.message || error.message || t("Error"),
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requiredFeedback = (
    <Form.Control.Feedback type="invalid">
      {t("required_field")}
    </Form.Control.Feedback>
  );

  const formFields = (
    <>
      {optionsError && (
        <div className="alert alert-danger" role="alert">
          {optionsError}
        </div>
      )}

      <Row className="sf-agri-form-section g-3">
        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-irrigation-zone" : "irrigation-zone"}
          >
            <Form.Label>{t("irrigation_zone")} *</Form.Label>
            <FarmSelect
              defaultval={{
                value: parentZoneId || zoneId,
                label: parentZoneName || zoneName,
              }}
              url="/zone/search-all-zones"
              onChange={(selected) =>
                handleFieldChange("zone_uid", selected?.value || "")
              }
              placeholder={t("Search zones...")}
              isDisabled={Boolean(isWizardMode && parentZoneId)}
            />
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-irrigation-crop" : "irrigation-crop"}
          >
            <Form.Label>{t("irrigation_crop")} *</Form.Label>
            <Form.Select
              value={irrigData.crop_uid || ""}
              onChange={(event) =>
                handleFieldChange("crop_uid", event.target.value)
              }
              required
              disabled={!irrigData.zone_uid}
            >
              <option value="">{t("select_crop")}</option>
              {crops.map((crop) => (
                <option key={crop.uid} value={String(crop.uid)}>
                  {getCropTypeName(crop.croptype_id)}
                </option>
              ))}
            </Form.Select>
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="4" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-irrigation-type" : "irrigation-type"}
          >
            <Form.Label>{t("Irrigation_system_type")} *</Form.Label>
            <Form.Select
              value={irrigData.irrigType}
              onChange={handleIrrigationType}
              required
              disabled={isOptionsLoading}
              aria-busy={isOptionsLoading}
            >
              <option value="">
                {isOptionsLoading ? t("loading") : t("select_irriagtion")}
              </option>
              {Object.keys(IRRIGATION_TYPE_MAP).map((type) => (
                <option key={type} value={type}>
                  {t(type)}
                </option>
              ))}
            </Form.Select>
            {requiredFeedback}
          </Form.Group>
        </Col>
      </Row>

      <Row className="sf-agri-form-section g-3">
        <Col lg="3" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-irrigation-efficiency" : "irrigation-efficiency"}
          >
            <Form.Label>{t("efficience_irrigation")} (%)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max="100"
              value={irrigData.effIrrig}
              onChange={(event) =>
                handleFieldChange("effIrrig", event.target.value)
              }
              placeholder={t("efficience_irrigation")}
            />
          </Form.Group>
        </Col>

        <Col lg="3" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-network-type" : "network-type"}
          >
            <Form.Label>{t("type_reseau")} *</Form.Label>
            <Form.Control
              required
              value={irrigData.pumpType}
              onChange={(event) =>
                handleFieldChange("pumpType", event.target.value)
              }
              placeholder={t("type_reseau")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="3" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-network-flow" : "network-flow"}
          >
            <Form.Label>{t("debit_reseau")} (l/s) *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              required
              value={irrigData.pumpFlow}
              onChange={(event) =>
                handleFieldChange("pumpFlow", event.target.value)
              }
              placeholder={t("debit_reseau")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>

        <Col lg="3" md="6" xs="12">
          <Form.Group
            controlId={isWizardMode ? "wizard-lines-number" : "lines-number"}
          >
            <Form.Label>{t("nbr_ligne")} *</Form.Label>
            <Form.Control
              required
              type="number"
              min="1"
              value={irrigData.linesNumber}
              onChange={(event) =>
                handleFieldChange("linesNumber", event.target.value)
              }
              placeholder={t("nbr_ligne")}
            />
            {requiredFeedback}
          </Form.Group>
        </Col>
      </Row>

      {irrigData.irrigType && (
        <Row className="sf-agri-form-section sf-agri-composite-section g-3">
          {irrigationMethodForm()}
        </Row>
      )}
    </>
  );

  return (
    <Container
      className={`${isWizardMode ? "" : "p-md-5 p-3"} sf-agri-form`}
    >
      {!isWizardMode && (
        <div className="mb-4">
          <PageTitle subtitle={t("Irrigation_info")} className="mb-3" />
          <div className="sf-agri-intro">
            <p className="sf-agri-help">{t("step_4_info")}</p>
          </div>
        </div>
      )}

      {isWizardMode ? (
        formFields
      ) : (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {formFields}
          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              className="sf-agri-submit"
              disabled={isSubmitting || isOptionsLoading}
            >
              {isSubmitting ? t("loading") : t("submit_form")}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
}
